import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { environment } from '../../environments/environment';

interface NativeDeviceIdentity {
  serial?: string;
  source?: string;
}

interface DeviceIdentityPlugin {
  getIdentity(): Promise<NativeDeviceIdentity>;
}

const DeviceIdentity = registerPlugin<DeviceIdentityPlugin>('DeviceIdentity');

/**
 * Silent appliance login for managed Android hardware.
 *
 * The existing Hardware Devices contract authenticates through
 * POST /temiDevices/getBySerial. Administrators register the device serial and
 * use that same serial as its activation password; the backend bcrypt-checks
 * the password and returns the same long-lived device user/session used by the
 * DineNGo Assistant app.
 *
 * Failure is intentionally quiet. An unregistered device, unavailable Android
 * serial, or temporary network error simply falls through to the normal login
 * screen — it never strands the cashier on a boot loader.
 */
@Injectable({ providedIn: 'root' })
export class DeviceIdentityService {
  constructor(private http: HttpClient) {}

  async authenticateRegisteredDevice(): Promise<any | null> {
    if (!Capacitor.isNativePlatform()) {
      return null;
    }

    try {
      const identity = await DeviceIdentity.getIdentity();
      const serial = (identity?.serial || '').trim();
      if (!serial) {
        return null;
      }

      return await this.http.post<any>(
        `${environment.apiUrl}/temiDevices/getBySerial`,
        { serialNum: serial, hash: serial },
      ).toPromise();
    } catch (_) {
      return null;
    }
  }
}
